import { useState, useMemo, useCallback } from 'react';
import ClientCard from './card';
import { ClientProps } from '@/types';
import { Empty } from './empty/Empty';
import ModalBoleto from './boleto-modal';
import { ModalCreditLimit } from '@/components';
import styled from 'styled-components';

const ListContainer = styled.div`
  position: relative;
  width: 100%;
  contain: layout style;
  will-change: scroll-position;
  transform: translateZ(0);
`;

type Props = {
  showProgress?: boolean;
  clients: ClientProps[];
  selectedClient?: ClientProps;
  setLocalClient: (value: ClientProps | undefined) => void;
  isModalOpen?: boolean;
};

type ModalState = { type: 'boleto'; client: ClientProps } | { type: 'credit'; client: ClientProps } | null;

const ListClients = ({ clients, showProgress, selectedClient, setLocalClient }: Props) => {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  // ⬇️ Estado global de modais (fora da lista/itens)
  const [modal, setModal] = useState<ModalState>(null);

  const localClients = useMemo(() => {
    const arr = [...(clients || [])];
    return arr.sort((a, b) => {
      if (a.ST_COMPRA === 'I' && b.ST_COMPRA !== 'I') return 1;
      if (a.ST_COMPRA !== 'I' && b.ST_COMPRA === 'I') return -1;
      return a.NM_CLIENTE?.localeCompare(b.NM_CLIENTE) || 0;
    });
  }, [clients]);

  const toggleItemExpansion = useCallback((clientId: number) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      next.has(clientId) ? next.delete(clientId) : next.add(clientId);
      return next;
    });
  }, []);

  const handleOpenBoleto = useCallback((c: ClientProps) => {
    setModal({ type: 'boleto', client: c });
  }, []);

  const handleOpenCreditLimit = useCallback((c: ClientProps) => {
    setModal({ type: 'credit', client: c });
  }, []);

  const hasClients = localClients.length > 0;

  return (
    <>
      {!hasClients ? (
        <Empty />
      ) : (
        <ListContainer>
          {localClients.map((client, index) => {
            const isActive = selectedClient?.COD_CLIENTE === client.COD_CLIENTE;
            return (
              <ClientCard
                index={index}
                key={client.COD_CLIENTE}
                client={client}
                isChecked={isActive}
                setClient={setLocalClient}
                showProgress={showProgress}
                isExpanded={expandedItems.has(client.COD_CLIENTE)}
                toggleExpansion={() => toggleItemExpansion(client.COD_CLIENTE)}
                onOpenBoleto={handleOpenBoleto}
                onOpenCreditLimit={handleOpenCreditLimit}
              />
            );
          })}
        </ListContainer>
      )}

      {/* ⬇️ Modais fora da lista (via Portal no componente Modal) */}
      {modal?.type === 'boleto' && <ModalBoleto client={modal.client} isOpen closeModal={() => setModal(null)} />}
      {modal?.type === 'credit' && <ModalCreditLimit isOpen closeModal={() => setModal(null)} />}
    </>
  );
};

export default ListClients;
